/* FaceTime-style fullscreen voice-call UI.
   Renders in three flavors based on CallContext phase:
     - dialing  → outgoing "ringing…" screen with cancel button
     - incoming → incoming-call sheet with accept/decline (rendered separately
                  below — IncomingCallSheet)
     - in_call  → live call screen with timer + mute + speaker + hang up

   Everything is positioned `fixed inset-0` with a very high z-index so it
   floats above any underlying page (MessageRoom, BottomNav, etc.). */
import { useEffect, useMemo, useState } from "react";
import {
  PhoneSlash,
  MicrophoneSlash,
  Microphone,
  SpeakerHigh,
  SpeakerSimpleNone,
} from "@phosphor-icons/react";
import { CALL_PHASE, useCall } from "../../context/CallContext";
import { playRing, stopRing } from "../../lib/ringer";

function Avatar({ name, size = 120 }) {
  const initial = (name || "؟").trim().charAt(0);
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#E6C463] to-[#B8860B] text-white font-bold flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.35)] ring-4 ring-white/10"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function fmtDuration(ms) {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function CallScreen() {
  const {
    phase,
    peer,
    callStartedAt,
    isMuted,
    isSpeakerOn,
    toggleMute,
    toggleSpeaker,
    hangup,
  } = useCall();

  // Live duration ticker for the in-call HUD.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (phase !== CALL_PHASE.IN_CALL) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Ringback tone for the CALLER while waiting for the other side to
  // pick up. Stops the moment the call connects or is closed.
  useEffect(() => {
    if (phase === CALL_PHASE.DIALING) {
      playRing("ringback");
      return () => stopRing();
    }
    return undefined;
  }, [phase]);

  const duration = useMemo(() => {
    if (phase !== CALL_PHASE.IN_CALL || !callStartedAt) return "00:00";
    return fmtDuration(Date.now() - callStartedAt);
    // tick intentionally drives the recompute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, callStartedAt, phase]);

  // Only render for dialing + in_call. Incoming is its own component.
  if (phase !== CALL_PHASE.DIALING && phase !== CALL_PHASE.IN_CALL) return null;

  const subtitle = phase === CALL_PHASE.DIALING
    ? "جارٍ الاتصال…"
    : duration;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      dir="rtl"
      data-testid="call-screen"
      style={{
        background:
          "radial-gradient(ellipse at top, #1a2940 0%, #0A192F 55%, #061226 100%)",
        color: "white",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Header label */}
      <div className="pt-6 text-center text-[13px] tracking-wide text-white/60">
        مكالمة صوتية
      </div>

      {/* Avatar + identity */}
      <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
        <Avatar name={peer?.name} size={140} />
        <div className="text-center">
          <div className="text-[24px] font-bold" data-testid="call-peer-name">
            {peer?.name || "مستخدم LexIQ"}
          </div>
          <div className="mt-1 text-[14px] text-white/70" data-testid="call-status">
            {subtitle}
          </div>
          {phase === CALL_PHASE.DIALING && (
            <div className="mt-6 flex items-center justify-center gap-1.5" aria-hidden>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full bg-white/70"
                  style={{
                    animation: `lex-call-pulse 1.2s ease-in-out ${i * 0.18}s infinite`,
                  }}
                />
              ))}
              <style>{`
                @keyframes lex-call-pulse {
                  0%,80%,100% { opacity: .3; transform: translateY(0); }
                  40% { opacity: 1; transform: translateY(-3px); }
                }
              `}</style>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="pb-10 px-8 flex items-center justify-center gap-5">
        {/* Mute + Speaker are useful as soon as the local mic / audio
            session is alive — which is from the moment the call starts
            DIALING (caller side joins Agora immediately). Showing them
            only during IN_CALL hid the buttons from the caller for the
            entire ringing phase. */}
        <button
          type="button"
          onClick={toggleMute}
          data-testid="call-mute"
          aria-label={isMuted ? "إلغاء الكتم" : "كتم"}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition active:scale-95 ${
            isMuted
              ? "bg-white text-[#0A192F]"
              : "bg-white/15 backdrop-blur text-white hover:bg-white/25"
          }`}
        >
          {isMuted ? (
            <MicrophoneSlash size={26} weight="bold" />
          ) : (
            <Microphone size={26} weight="bold" />
          )}
        </button>

        {/* Speaker / Earpiece toggle — WhatsApp-style 🔈 button. Defaults
            to earpiece (private, phone-style audio routing); tapping
            switches to loudspeaker for hands-free / group listening. */}
        <button
          type="button"
          onClick={toggleSpeaker}
          data-testid="call-speaker"
          aria-label={isSpeakerOn ? "إيقاف مكبّر الصوت" : "تشغيل مكبّر الصوت"}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition active:scale-95 ${
            isSpeakerOn
              ? "bg-white text-[#0A192F]"
              : "bg-white/15 backdrop-blur text-white hover:bg-white/25"
          }`}
        >
          {isSpeakerOn ? (
            <SpeakerHigh size={26} weight="bold" />
          ) : (
            <SpeakerSimpleNone size={26} weight="bold" />
          )}
        </button>

        <button
          type="button"
          onClick={hangup}
          data-testid="call-hangup"
          aria-label="إنهاء المكالمة"
          className="w-20 h-20 rounded-full bg-[#E0413B] flex items-center justify-center shadow-[0_10px_40px_rgba(224,65,59,0.55)] active:scale-95 transition"
        >
          <PhoneSlash size={32} weight="fill" color="white" />
        </button>
      </div>
    </div>
  );
}
